/**
 * Every reading of the scan on the board, a row each, counted the same way.
 *
 * The table is what the board is for: two readers are told apart by a number, and only where
 * the number is come by the same way for both. So the columns are the count's own and no
 * reader's, and a reading not yet counted, or counted against nothing, shows dashes rather
 * than a nought. The row lit is the reading on the desk; a click on another puts it there.
 */

import type { PageSpan } from "@app/shared-types";
import { count, rate } from "../lib/format";
import { onPage, sharesOf } from "../lib/read-count";
import type { Counted, Run, Shown } from "../lib/read-runs";

/** How long a reading took, as a person says it. */
function took(seconds: number): string {
  const whole = Math.round(seconds);
  return whole < 60 ? `${whole} s` : `${Math.floor(whole / 60)} min ${whole % 60} s`;
}

const HEAD =
  "px-2.5 py-1 text-left font-mono text-[10px] font-normal uppercase tracking-[0.1em] text-shell-dim";
const CELL = "px-2.5 py-1 font-mono text-[11px]";

/** What is said under the table about the count of the reading on the desk. */
function footnote(truth: File | null, counted: Counted | undefined, span: PageSpan | null): string {
  if (truth === null) {
    return "Give the true score to count a reading against it: a note is found where the reading has its written pitch at its moment in the bar.";
  }
  if (counted === undefined) return `Counting against ${truth.name}…`;
  if (typeof counted === "string") return `Not counted against ${truth.name}: ${counted}`;
  const stretch =
    counted.first === null || counted.last === null
      ? "The reading holds none of the piece."
      : `Right is counted over the piece's stretch of the reading, bars ${counted.first}–${counted.last}.`;
  const page = span === null ? null : onPage(counted, span);
  return `Counted against ${truth.name}, ${count(counted.truthNotes)} notes in ${count(counted.truthBars)} bars: a note is found where the reading has its written pitch at its moment in the bar. ${stretch}${
    page === null
      ? ""
      : ` On this page ${count(page.right)} of ${count(page.notes)} notes read are the score's.`
  }`;
}

export interface ReadRunsProps {
  readonly runs: readonly Run[];
  readonly shown: Shown | null;
  readonly counts: Readonly<Record<number, Counted>>;
  readonly truth: File | null;
  /** The bars of the page on the desk in the reading shown, where it says which they are. */
  readonly span: PageSpan | null;
  onShow(id: number): void;
}

export default function ReadRuns({
  runs,
  shown,
  counts,
  truth,
  span,
  onShow,
}: ReadRunsProps): JSX.Element {
  return (
    <section className="max-h-[14rem] shrink-0 overflow-y-auto border-t border-shell-edge bg-shell-bar">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-shell-edge">
            {["Read by", "Took", "Notes read", "Found", "With its length", "Right", "F1"].map(
              (title) => (
                <th key={title} scope="col" className={HEAD}>
                  {title}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => {
            const counted = counts[run.id];
            const shares =
              counted === undefined || typeof counted === "string" ? null : sharesOf(counted);
            const lit = run === shown;
            return (
              <tr
                key={run.id}
                onClick={() => onShow(run.id)}
                className={`cursor-pointer border-b border-shell-edge ${
                  lit ? "bg-shell-key text-shell-lit" : "text-shell-text hover:bg-shell-hot"
                }`}
              >
                <th scope="row" className="px-2.5 py-1 text-left text-[11px] font-medium">
                  <button type="button" aria-pressed={lit} onClick={() => onShow(run.id)}>
                    {run.reader.label}
                  </button>
                </th>
                <td className={CELL}>{took(run.reading.seconds)}</td>
                <td className={CELL}>{count(run.reading.notes)}</td>
                <td className={CELL}>{rate(shares?.found)}</td>
                <td className={CELL}>{rate(shares?.withLength)}</td>
                <td className={CELL}>{rate(shares?.right)}</td>
                <td className={CELL}>{rate(shares?.f1)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="px-2.5 py-1.5 text-[11px] leading-relaxed text-shell-dim">
        {footnote(truth, shown === null ? undefined : counts[shown.id], span)}
      </p>
    </section>
  );
}
